import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { contactInfo } from '../mockData';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Use FormData to match Web3Forms requirements
      const formElement = e.target;
      const formDataObj = new FormData(formElement);

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formDataObj
      });

      const data = await response.json();

      if (data.success) {
        toast.success("Zpráva odeslána!", {
          description: "Děkuji za vaši zprávu. Brzy se vám ozvu.",
        });
        
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      } else {
        throw new Error(data.message || 'Odeslání se nezdařilo');
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Chyba při odesílání", {
        description: "Zkuste to prosím znovu nebo mě kontaktujte přímo na telefonu 737 173 595",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-white via-gray-50 to-gray-100 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header - Enhanced */}
        <div className="text-center mb-20">
          <div className="inline-block mb-4">
            <span className="px-6 py-2 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 text-green-600 font-semibold text-sm tracking-wider uppercase">
              Kontakt
            </span>
          </div>
          <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6 bg-gradient-to-r from-gray-900 via-blue-900 to-gray-900 bg-clip-text text-transparent">
            Kontaktujte nás
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Máte dotaz nebo chcete objednat službu? Rád vám pomůžu!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information - Enhanced */}
          <div className="space-y-6">
            <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-3xl font-black bg-gradient-to-r from-gray-900 to-blue-900 bg-clip-text text-transparent">
                  Kontaktní informace
                </CardTitle>
                <CardDescription className="text-base">Neváhejte mě kontaktovat</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Phone - Enhanced */}
                <a
                  href={`tel:${contactInfo.phone}`}
                  className="flex items-center gap-5 p-5 rounded-2xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 transition-all duration-300 group"
                >
                  <div className="bg-gradient-to-br from-blue-600 to-blue-500 text-white p-4 rounded-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <Phone size={26} strokeWidth={2.5} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-semibold mb-1">Telefon</p>
                    <p className="text-xl font-black text-gray-900 group-hover:text-blue-600 transition-colors">
                      {contactInfo.phone}
                    </p>
                  </div>
                </a>

                {/* Email - Enhanced */}
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-center gap-5 p-5 rounded-2xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 transition-all duration-300 group"
                >
                  <div className="bg-gradient-to-br from-blue-600 to-blue-500 text-white p-4 rounded-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <Mail size={26} strokeWidth={2.5} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-semibold mb-1">Email</p>
                    <p className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors break-all">
                      {contactInfo.email}
                    </p>
                  </div>
                </a>

                {/* Address - Enhanced */}
                <div className="flex items-center gap-5 p-5 rounded-2xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 transition-all duration-300">
                  <div className="bg-gradient-to-br from-blue-600 to-blue-500 text-white p-4 rounded-2xl shadow-lg">
                    <MapPin size={26} strokeWidth={2.5} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-semibold mb-1">Adresa</p>
                    <p className="text-xl font-black text-gray-900">
                      {contactInfo.address}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Opening Hours - Enhanced */}
            <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 bg-gradient-to-br from-blue-600 to-cyan-600 text-white">
              <CardHeader>
                <CardTitle className="text-2xl font-black text-white">Provozní doba</CardTitle>
                <CardDescription className="text-blue-100 font-semibold">Po domluvě</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-white leading-relaxed text-base">
                  Služby poskytuju po předchozí domluvě. Volejte nebo pište pro stanovení termínu, který vám bude vyhovovat.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form - Enhanced */}
          <Card className="border-0 shadow-2xl hover:shadow-3xl transition-all duration-500 bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-3xl font-black bg-gradient-to-r from-gray-900 to-blue-900 bg-clip-text text-transparent">
                Napište mi
              </CardTitle>
              <CardDescription className="text-base">Vyplňte formulář a já se vám brzy ozvu</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Hidden Web3Forms fields */}
                <input type="hidden" name="access_key" value="a9e0728c-b2e7-4a69-8af9-f50d6b93fcfb" />
                <input type="hidden" name="subject" value="Nová poptávka z webu Čištění vozidel Kuja" />
                
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-bold text-gray-700">Jméno a příjmení *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Jan Novák"
                    className="border-2 focus:border-blue-500 h-12 rounded-xl text-base transition-all duration-300"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-bold text-gray-700">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="jan.novak@email.cz"
                    className="border-2 focus:border-blue-500 h-12 rounded-xl text-base transition-all duration-300"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-bold text-gray-700">Telefon</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="123 456 789"
                    className="border-2 focus:border-blue-500 h-12 rounded-xl text-base transition-all duration-300"
                  />
                </div>

                {/* Service Selection Dropdown */}
                <div className="space-y-2">
                  <Label htmlFor="service" className="text-sm font-bold text-gray-700">Vyberte službu *</Label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full border-2 focus:border-blue-500 h-12 rounded-xl text-base transition-all duration-300 px-3 bg-white"
                  >
                    <option value="">-- Vyberte službu --</option>
                    <option value="Kompletní čištění interiéru">Kompletní čištění interiéru</option>
                    <option value="Umytí exteriéru">Umytí exteriéru</option>
                    <option value="Umytí kol">Umytí kol</option>
                    <option value="Základní čištění interiéru">Základní čištění interiéru</option>
                    <option value="Tepování sedadla">Tepování sedadla</option>
                    <option value="Pouze dotaz">Pouze dotaz</option>
                  </select>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-sm font-bold text-gray-700">Zpráva *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Popište požadovanou službu nebo položte dotaz..."
                    rows={5}
                    className="border-2 focus:border-blue-500 resize-none rounded-xl text-base transition-all duration-300"
                  />
                </div>

                {/* Submit Button - Enhanced */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white text-lg py-7 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl font-bold"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Odesílám...
                    </span>
                  ) : (
                    <>
                      <Send className="mr-2" size={20} />
                      Odeslat zprávu
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Google Map Section */}
        <div className="mt-12">
          <Card className="border-0 shadow-2xl overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
              <CardTitle className="text-2xl font-black flex items-center gap-3">
                <MapPin size={28} />
                Kde mě najdete
              </CardTitle>
              <CardDescription className="text-blue-100 font-semibold">
                Příční 29, Prušánky
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <iframe
                src="https://www.google.com/maps?q=Příční+29,+Prušánky&output=embed"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Mapa - Čištění vozidel Kuja"
                className="w-full"
              ></iframe>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
